import { getRequiredElement } from './dom';
import type { HistorySnapshot } from '../core/types';

let toastTimeout: ReturnType<typeof setTimeout> | null = null;
let historyBuffer: HistorySnapshot | null = null;
let undoCallback: ((snapshot: HistorySnapshot) => void) | null = null;

/**
 * Registers an undo callback to be invoked when the user clicks "Undo" inside the toast.
 */
export function registerUndoHandler(callback: (snapshot: HistorySnapshot) => void): void {
  undoCallback = callback;
}

/**
 * Sets the snapshot to restore if the user clicks Undo.
 */
export function setHistorySnapshot(snapshot: HistorySnapshot): void {
  historyBuffer = snapshot;
}

/**
 * Displays an animated floating toast notification.
 *
 * @param msg HTML string or plain text to display.
 * @param duration Duration in milliseconds before fading out (default: 2800ms).
 */
export function showToast(msg: string, duration = 2800): void {
  const toast = getRequiredElement<HTMLDivElement>('toast');

  toast.innerHTML = msg;
  toast.classList.add('show');

  // Attach click handler to any undo links inside the toast
  const undoBtn = toast.querySelector('.undo-action');
  if (undoBtn) {
    undoBtn.addEventListener(
      'click',
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        triggerUndo();
      },
      { once: true }
    );
  }

  if (toastTimeout) {
    clearTimeout(toastTimeout);
  }

  toastTimeout = setTimeout(() => {
    toast.classList.remove('show');
  }, duration);
}

/**
 * Returns whether an undo snapshot is currently available.
 */
export function hasHistorySnapshot(): boolean {
  return historyBuffer !== null;
}

/**
 * Triggers undo action using the cached snapshot.
 */
export function triggerUndo(): void {
  if (!historyBuffer || !undoCallback) return;
  const snapshot = historyBuffer;
  historyBuffer = null;
  undoCallback(snapshot);
}
